import React, { FC, useState, useRef, useEffect } from 'react';

interface TimerInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  ms?: number;
  placeholder?: string;
  className?: string;
  icon?: string;
  disabled?: boolean;
}

const TailwindClearableTimerInput: FC<TimerInputProps> = ({
  value = '',
  onChange,
  onKeyPress,
  ms = 1000,
  placeholder,
  className = '',
  icon,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const timerRef = useRef<NodeJS.Timeout>();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);

    clearTimeout(timerRef.current);

    if (newValue === '') {
      onChange?.(newValue);
    } else {
      timerRef.current = setTimeout(() => {
        onChange?.(newValue);
      }, ms);
    }
  };

  const handleClear = () => {
    setInputValue('');
    onChange?.('');
    clearTimeout(timerRef.current);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      clearTimeout(timerRef.current);
      onChange?.(inputValue);
    }
    onKeyPress?.(event);
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <div className="relative">
      {icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <i className={`${icon} text-gray-400`} />
        </div>
      )}

      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        className={`
          w-full px-3 py-2 
          border border-gray-300 
          rounded-md
          focus:outline-none 
          focus:ring-2 
          focus:ring-blue-500 
          focus:border-blue-500
          disabled:bg-gray-100 
          disabled:cursor-not-allowed
          ${icon ? 'pl-10' : ''}
          ${inputValue ? 'pr-10' : ''}
          ${className}
        `}
      />

      {inputValue && !disabled && (
        <button onClick={handleClear} className="absolute inset-y-0 right-0 pr-3 flex items-center" type="button">
          <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default TailwindClearableTimerInput;
